import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn.ts";

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  /** Draws a separator above this item. */
  divider?: boolean;
  disabled?: boolean;
}

/** Small click-to-open dropdown (the ⋯ menu on chat rows). Closes on outside click and Escape. */
export function Menu({ trigger, items, align = "end", className }: { trigger: ReactNode; items: MenuItem[]; align?: "start" | "end"; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  return (
    <div ref={ref} className={cn("relative", className)}>
      <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}>{trigger}</span>
      {open && (
        <div role="menu" className={cn("absolute top-full z-30 mt-1 min-w-56 rounded-xl border border-border bg-surface p-1 text-sm shadow-xl", align === "end" ? "end-0" : "start-0")}>
          {items.map((it, i) => (
            <div key={i}>
              {it.divider && <div className="my-1 h-px bg-border" />}
              <button type="button" role="menuitem" disabled={it.disabled} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); it.onSelect(); }} className={cn("flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start hover:bg-surface-2 disabled:opacity-40", it.danger ? "text-danger" : "text-fg")}>
                {it.icon && <span className="grid size-4 place-items-center text-fg-muted">{it.icon}</span>}
                {it.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
